/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T11:40:00-08:00
Intent: Date picker shown as dd/MM/yy whose form value stays a plain YYYY-MM-DD string.
*/
import { Component, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';

/**
 * A native <input type="date"> shows its value in the browser's own locale (11/03/2026 on an
 * en-US machine) and cannot be told otherwise, so the receipt purchase window uses the same
 * nz-date-picker as the rest of the campaign form instead.
 *
 * nz-date-picker hands back Date objects, while the receipt rules — their validation, the save
 * payload, the backend — all work with "YYYY-MM-DD". This keeps the form value in that shape.
 * The conversion uses LOCAL date parts both ways: new Date('2030-01-01') is UTC midnight, which
 * shows as the day before anywhere west of Greenwich.
 */
@Component({
  selector: 'app-iso-date-picker',
  standalone: true,
  imports: [FormsModule, NzDatePickerModule],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => IsoDatePickerComponent), multi: true },
  ],
  template: `
    <nz-date-picker class="custom-select" style="width: 100%"
      [nzId]="inputId" [nzFormat]="format" [nzPlaceHolder]="placeholder" [nzDisabled]="disabled"
      [ngModel]="date" (ngModelChange)="onPicked($event)" (nzOnOpenChange)="!$event && onTouched()">
    </nz-date-picker>
  `,
})
export class IsoDatePickerComponent implements ControlValueAccessor {
  @Input() inputId = '';
  @Input() format = 'dd/MM/yy';
  @Input() placeholder = 'DD/MM/YY';

  date: Date | null = null;
  disabled = false;

  private onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: unknown): void {
    this.date = isoToLocalDate(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onPicked(value: Date | null): void {
    this.date = value;
    this.onChange(localDateToIso(value));
  }
}

/** "YYYY-MM-DD" (or a timestamp starting with it) -> that calendar day at local midnight. */
export function isoToLocalDate(value: unknown): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? '').trim());
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A Date -> its local calendar day as "YYYY-MM-DD". */
export function localDateToIso(value: Date | null | undefined): string | null {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}
